// src/views/StoriesView.js — Indian Watch Stories (YouTube wall)
import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useLanguage, t } from "../lib/i18n";

function extractYTId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function timeAgo(iso) {
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 60)  return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return `${Math.floor(m/1440)}d ago`;
}

// ─── Single story card ────────────────────────────────────────────────────────

function StoryCard({ story, liked, onLike, session, openAuth }) {
  const [playing, setPlaying] = useState(false);
  const ytId = story.youtube_id || extractYTId(story.youtube_url);

  return (
    <div style={{background:"#fff",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,.06)",border:"1.5px solid var(--bd)",display:"flex",flexDirection:"column"}}>
      {/* Thumbnail / embed */}
      <div style={{position:"relative",paddingTop:"56.25%",background:"#111118",cursor:playing?"default":"pointer"}}
           onClick={()=>!playing&&setPlaying(true)}>
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
            allow="autoplay; encrypted-media"
            allowFullScreen
            style={{position:"absolute",inset:0,width:"100%",height:"100%",border:"none"}}
            title={story.title}/>
        ) : (
          <>
            <img
              src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
              alt={story.title}
              style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
            {/* Play button overlay */}
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.28)"}}>
              <div style={{width:52,height:52,borderRadius:"50%",background:"rgba(255,255,255,.92)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 16px rgba(0,0,0,.3)"}}>
                <div style={{width:0,height:0,borderTop:"10px solid transparent",borderBottom:"10px solid transparent",borderLeft:"18px solid #111118",marginLeft:4}}/>
              </div>
            </div>
            {story.featured && (
              <div style={{position:"absolute",top:10,left:10,background:"#D97706",color:"#fff",fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:"0.12em",padding:"3px 8px",borderRadius:4}}>
                ✦ FEATURED
              </div>
            )}
          </>
        )}
      </div>

      {/* Info */}
      <div style={{padding:"14px 16px",flex:1,display:"flex",flexDirection:"column"}}>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontStyle:"italic",lineHeight:1.3,marginBottom:6,color:"#111118"}}>{story.title}</div>
        {story.caption && <p style={{fontSize:12.5,color:"var(--ink3)",lineHeight:1.65,margin:"0 0 10px",flex:1}}>{story.caption}</p>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"auto",paddingTop:8,borderTop:"1px solid var(--bd)"}}>
          <div>
            {story.submitted_by && <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"var(--ink2)",fontWeight:500}}>{story.submitted_by}</div>}
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginTop:2}}>{timeAgo(story.created_at)}</div>
          </div>
          <button
            onClick={()=>{ if(!session){openAuth();return;} onLike(story.id); }}
            style={{background:liked?"#FEF3C7":"transparent",border:`1.5px solid ${liked?"#D97706":"var(--bd)"}`,borderRadius:20,padding:"5px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:5,transition:"all .15s"}}>
            <span style={{fontSize:14}}>{liked?"♥":"♡"}</span>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:liked?"#D97706":"var(--ink3)"}}>
              {(story.like_count||0)+(liked&&!(story.liked_by_me)?1:0)}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function StoriesView({ session, openAuth }) {
  const { lang } = useLanguage();
  const [stories, setStories]   = useState([]);
  const [likes, setLikes]       = useState(new Set()); // story_ids liked by this user
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("hmt_stories")
      .select("*")
      .order("featured", { ascending: false })
      .order("created_at", { ascending: false });
    setStories(data || []);

    if (session?.user?.id) {
      const { data: likeData } = await supabase
        .from("story_likes")
        .select("story_id")
        .eq("user_id", session.user.id);
      setLikes(new Set((likeData||[]).map(l => l.story_id)));
    }
    setLoading(false);
  }, [session?.user?.id]);

  useEffect(() => { load(); }, [load]);

  const handleLike = async (storyId) => {
    const uid = session.user.id;
    const alreadyLiked = likes.has(storyId);
    setLikes(prev => {
      const n = new Set(prev);
      alreadyLiked ? n.delete(storyId) : n.add(storyId);
      return n;
    });
    if (alreadyLiked) {
      await supabase.from("story_likes").delete().eq("story_id", storyId).eq("user_id", uid);
    } else {
      await supabase.from("story_likes").insert({ id:`sl-${Date.now()}`, story_id: storyId, user_id: uid, created_at: new Date().toISOString() });
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{marginBottom:32}}>
        <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.2em",color:"var(--accent)",textTransform:"uppercase"}}>{t("stories_community",lang)}</span>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:38,margin:"6px 0 8px",letterSpacing:"-0.025em"}}>{t("stories_head",lang)}</h1>
        <p style={{fontSize:14,color:"var(--ink3)",lineHeight:1.75,maxWidth:520,margin:0}}>
          {t("stories_sub",lang)}
        </p>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:"80px 0",fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.1em"}}>{t("stories_loading",lang).toUpperCase()}</div>
      ) : stories.length === 0 ? (
        <div style={{textAlign:"center",padding:"80px 24px",background:"var(--bg2)",borderRadius:20}}>
          <div style={{fontSize:40,marginBottom:16}}>🎬</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontStyle:"italic",marginBottom:8}}>No stories yet</div>
          <p style={{color:"var(--ink3)",fontSize:13,lineHeight:1.7,maxWidth:320,margin:"0 auto"}}>The admin curates watch stories from the community. Check back soon.</p>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:18}}>
          {stories.map(s => (
            <StoryCard key={s.id} story={s}
              liked={likes.has(s.id)}
              onLike={handleLike}
              session={session}
              openAuth={openAuth}/>
          ))}
        </div>
      )}
    </div>
  );
}
